import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export interface AcknowledgeTarget {
  id: string;
  number: string;
  studyType: { name: string };
  patient: { fullName: string; mrn: string };
}

const TOLD_MIN = 2;
const TOLD_MAX = 120;
const NOTE_MIN = 3;
const NOTE_MAX = 500;

export function RadiologyAcknowledgeDialog({ study, onClose }: { study: AcknowledgeTarget; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [toldName, setToldName] = useState('');
  const [readBackNote, setReadBackNote] = useState('');

  const told = toldName.trim();
  const note = readBackNote.trim();
  const valid = told.length >= TOLD_MIN && told.length <= TOLD_MAX && note.length >= NOTE_MIN && note.length <= NOTE_MAX;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
    void queryClient.invalidateQueries({ queryKey: ['radiology-study', study.id] });
  };

  const acknowledge = useMutation({
    mutationFn: () => post(`/radiology/studies/${study.id}/acknowledge`, { toldName: told, readBackNote: note }),
    onSuccess: () => {
      toast.success(tr('radiology', 'critical_finding_acknowledged'), tr('radiology', 'x_was_told', { told: told }));
      refresh();
      onClose();
    },
    onError: (error) => {
      toast.error(tr('radiology', 'could_not_record_the_acknowledgement'), describeError(error));
      if (error instanceof ApiError && error.status === 409) {
        refresh();
        onClose();
      }
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('radiology', 'acknowledge_critical_finding')}
      description={`${study.patient.fullName} · ${study.patient.mrn} · ${study.studyType.name} · ${study.number}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('radiology', 'cancel')}
          </Button>
          <Button variant="destructive" onClick={() => acknowledge.mutate()} loading={acknowledge.isPending} disabled={!valid}>
            {tr('radiology', 'record_acknowledgement')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">
          {tr('radiology', 'record_who_received_the_critical_finding_and')}
        </Alert>
        <Field label={tr('radiology', 'who_was_told')} required hint={tr('radiology', 'name_and_role_of_the_person_you_told_x_x', { TOLD_MIN: TOLD_MIN, TOLD_MAX: TOLD_MAX })}>
          <Input value={toldName} maxLength={TOLD_MAX} onChange={(e) => setToldName(e.target.value)} placeholder={tr('radiology', 'e_g_dr_ahmed_ward_4')} />
        </Field>
        <Field label={tr('radiology', 'read_back_note')} required hint={tr('radiology', 'what_they_repeated_back_to_you_x_x_characters', { NOTE_MIN: NOTE_MIN, NOTE_MAX: NOTE_MAX })}>
          <Textarea rows={3} value={readBackNote} maxLength={NOTE_MAX} onChange={(e) => setReadBackNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
